import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebaseconf';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { fetchUserRoleDocumentAfterLogin } from '../utils/authUtils';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [institutionId, setInstitutionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [roleLoading, setRoleLoading] = useState(false);

    useEffect(() => {
        // Check session storage first for persisted data
        const storedUser = sessionStorage.getItem('logged_user');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                setCurrentUser(user);
                setUserRole(user.role);
                setInstitutionId(user.institution_id || user.institutionId);
            } catch (err) {
                console.error('Failed to parse stored user:', err);
                sessionStorage.removeItem('logged_user');
            }
        }

        // Listen to Firebase auth state changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // User is authenticated with Firebase
                // Try to fetch their role from Firestore users collection
                try {
                    setRoleLoading(true);
                    const userRoleDoc = await fetchUserRoleDocumentAfterLogin(firebaseUser.uid);
                    
                    if (userRoleDoc) {
                        // Update context with complete user data - properly mapped for components
                        const userData = {
                            id: firebaseUser.uid,
                            uid: firebaseUser.uid,
                            email: userRoleDoc.email || firebaseUser.email,
                            name: userRoleDoc.name || firebaseUser.displayName || 'User',
                            role: userRoleDoc.role,
                            institution_id: userRoleDoc.institutionId,
                            institutionId: userRoleDoc.institutionId,
                            profileId: userRoleDoc.profileId,
                            phone: userRoleDoc.phone || '',
                            status: userRoleDoc.status || 'active',
                            createdAt: userRoleDoc.createdAt,
                            loginTime: new Date().toISOString()
                        };
                        
                        // Also save extracted branch for convenience
                        if (userRoleDoc.profileId) {
                            const parts = userRoleDoc.profileId.split('_');
                            if (parts.length >= 2) {
                                userData.branch = parts[1].toUpperCase();
                            }
                        }
                        
                        setCurrentUser(userData);
                        setUserRole(userRoleDoc.role);
                        setInstitutionId(userRoleDoc.institutionId);
                        
                        // Persist in sessionStorage
                        sessionStorage.setItem('logged_user', JSON.stringify(userData));
                    } else {
                        // Firebase user exists but no Firestore role document
                        console.warn('Firebase user exists but no role document in Firestore');
                        setCurrentUser(null);
                        setUserRole(null);
                        setInstitutionId(null);
                        sessionStorage.removeItem('logged_user');
                    }
                } catch (err) {
                    console.error('Error fetching user role:', err);
                    // Keep Firebase user authenticated but no role info
                    setCurrentUser(null);
                    setUserRole(null);
                    sessionStorage.removeItem('logged_user');
                } finally {
                    setRoleLoading(false);
                }
            } else {
                // User is NOT authenticated
                setCurrentUser(null);
                setUserRole(null);
                setInstitutionId(null);
                sessionStorage.removeItem('logged_user');
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const logout = async () => {
        try {
            setError(null);
            sessionStorage.removeItem('logged_user');
            sessionStorage.removeItem('user_role');
            setCurrentUser(null);
            setUserRole(null);
            setInstitutionId(null);
            
            try {
                await firebaseSignOut(auth);
            } catch (firebaseError) {
                console.error('Firebase logout error:', firebaseError);
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    /**
     * Set user info manually (used after successful login)
     * This updates both context and sessionStorage
     */
    const setUserInfo = (user) => {
        if (user) {
            setCurrentUser(user);
            setUserRole(user.role);
            setInstitutionId(user.institution_id || user.institutionId);
            // Store in sessionStorage for persistence
            sessionStorage.setItem('logged_user', JSON.stringify(user));
        }
    };

    const value = {
        currentUser,
        userRole,
        institutionId,
        loading: loading || roleLoading,
        error,
        logout,
        setUserInfo,
        isAuthenticated: !!currentUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
